import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTableProvider } from 'components/DataTable'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { cn } from 'components/UI/cn'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { useGetCampaignStats } from 'features/campaigns/campaign_stats_api'
import { CampaignsStatsBar } from 'features/campaigns/CampaignsStatsBar'
import { CampaignsTable } from 'features/campaigns/CampaignsTable'
import { CreateCampaignModalV2 } from 'features/campaigns/CreateCampaignModalV2'
import { CreateEditCampaignModal } from 'features/campaigns/CreateEditCampaignModal'
import { Campaign } from 'models/campaign'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export function CampaignsPage() {
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showV2Modal, setShowV2Modal] = useState<boolean>(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>()
  const [activeTab, setActiveTab] = useState<'campaigns' | 'replies'>(
    'campaigns'
  )

  function handleRowEdit(campaign: Campaign) {
    setShowModal(true)
    setSelectedCampaign(campaign)
  }

  function openModal() {
    if (featureFlag.enableEmailSequencing) {
      setShowV2Modal(true)
    } else {
      setShowModal(true)
    }
  }

  function hideModal() {
    setShowModal(false)
    setSelectedCampaign(undefined)
  }

  const { data: stats } = useGetCampaignStats(featureFlag.enableEmailSequencing)

  if (!featureFlag.isLoading && !featureFlag.enableCampaigns) {
    navigate('/')
    return null
  }

  if (featureFlag.enableEmailSequencing) {
    return (
      <DataTableProvider tableKey="campaign-overview-table">
        <FBPage>
          <FBPageHeader>
            <FBPageTitleRow>
              <div>
                <FBPageTitle>Campaigns</FBPageTitle>
                <p className="text-sm text-fg-secondary mt-0.5">
                  Manage your outreach campaigns from start to finish.
                </p>
              </div>
            </FBPageTitleRow>
            <FbButton onClick={() => openModal()}>+ New Campaign</FbButton>
          </FBPageHeader>

          <div className="pt-3">
            <CampaignsStatsBar stats={stats} />

            {/* Tabs */}
            <div className="flex gap-6 border-b border-line mb-3">
              <button
                className={cn(
                  'pb-2 text-sm font-medium transition-colors',
                  activeTab === 'campaigns'
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-fg-secondary hover:text-fg'
                )}
                onClick={() => setActiveTab('campaigns')}
              >
                <span className="flex items-center gap-2">
                  All Campaigns
                  {stats?.total_campaigns != null && (
                    <span className="bg-surface-hover text-fg-secondary text-xs px-1.5 py-px rounded-full">
                      {stats.total_campaigns}
                    </span>
                  )}
                </span>
              </button>
              <button
                className={cn(
                  'pb-2 text-sm font-medium transition-colors',
                  activeTab === 'replies'
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-fg-secondary hover:text-fg'
                )}
                onClick={() => setActiveTab('replies')}
              >
                All Replies
              </button>
            </div>

            {activeTab === 'replies' ? (
              <div className="flex items-center justify-center py-16 text-fg-secondary text-sm">
                Unified replies inbox coming soon.
              </div>
            ) : (
              <FBPageTableContainer
                style={{
                  height: 'calc(var(--base-page-table-height) - 200px)',
                }}
              >
                <CampaignsTable
                  onEditCampaign={handleRowEdit}
                  tableKey={ALL_CAMPAIGNS_TABLE_KEY}
                />
              </FBPageTableContainer>
            )}
          </div>
        </FBPage>

        <CreateEditCampaignModal
          isOpen={showModal}
          onClose={hideModal}
          editingCampaign={selectedCampaign}
        />
        <CreateCampaignModalV2
          show={showV2Modal}
          onHide={() => setShowV2Modal(false)}
        />
      </DataTableProvider>
    )
  }

  return (
    <DataTableProvider tableKey="campaign-overview-table">
      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>
              <span className={'font-normal'}>Campaigns: </span> All
            </FBPageTitle>
          </FBPageTitleRow>
          <FbButton style={{ width: 200 }} onClick={() => openModal()}>
            Create Campaign
          </FbButton>
        </FBPageHeader>
        <FBPageTableContainer
          style={{
            height: 'calc(var(--base-page-table-height) - 47px)',
          }}
        >
          <CampaignsTable
            onEditCampaign={handleRowEdit}
            tableKey={ALL_CAMPAIGNS_TABLE_KEY}
          />
        </FBPageTableContainer>
      </FBPage>
      <CreateEditCampaignModal
        isOpen={showModal}
        onClose={hideModal}
        editingCampaign={selectedCampaign}
      />
      <CreateCampaignModalV2
        show={showV2Modal}
        onHide={() => setShowV2Modal(false)}
      />
    </DataTableProvider>
  )
}
